import type { ReactNode } from 'react'
import { cn } from '../../lib/utils'

export interface DashboardStatCardProps {
	title:    string
	value:    ReactNode
	icon?:    ReactNode
	iconClassName?: string  // chip colours, e.g. 'bg-orange-100 text-orange-500'
	change?:  string
	trend?:   'up' | 'down'
	progress?: number       // 0..1 для прогресс-бара
	progressVariant?: 'default' | 'danger'
	footerLabel?: string
	footerValue?: ReactNode
	valueColor?: 'default' | 'danger'
	className?: string
	headerSlot?: ReactNode  // например, для селектора в правом верхнем углу
	/** Hover styling for a card wrapped in a link; the link itself is the caller's. */
	interactive?: boolean
}

const TrendArrow = ({ direction, className }: { direction: 'up' | 'down'; className?: string }) => (
	<span aria-hidden className={cn('inline-block leading-none', className)}>
		{direction === 'up' ? '↗' : '↘'}
	</span>
)

export function DashboardStatCard({
	title,
	value,
	icon,
	iconClassName,
	change,
	trend,
	progress,
	progressVariant = 'default',
	footerLabel,
	footerValue,
	valueColor = 'default',
	headerSlot,
	className,
	interactive,
}: DashboardStatCardProps) {
	const clampedProgress = progress != null ? Math.max(0, Math.min(1, progress)) : null
	return (
		<div
			className={cn(
				'flex flex-col gap-3 rounded-(--radius-lg) border border-(--color-border) bg-(--color-background) p-4',
				interactive && 'cursor-pointer transition-colors hover:border-(--color-foreground)/30 hover:bg-(--color-muted)/40',
				className,
			)}
		>
			<div className="flex items-start justify-between gap-2">
				{icon && (
					<span className={cn('flex size-10 items-center justify-center rounded-lg bg-(--color-muted) text-(--color-foreground) [&_svg]:size-5', iconClassName)}>
						{icon}
					</span>
				)}
				<div className="ml-auto flex items-center gap-2">
					{change && trend && (
						<span
							className={cn(
								'flex items-center gap-1 text-sm leading-5',
								trend === 'up' ? 'text-(--color-success)' : 'text-(--color-destructive)',
							)}
						>
							<TrendArrow direction={trend} />
							{change}
						</span>
					)}
					{headerSlot}
				</div>
			</div>

			<div className="flex flex-col gap-1">
				<span
					className={cn(
						'text-4xl font-[600] leading-10 tracking-[-1px]',
						valueColor === 'danger' ? 'text-(--color-destructive)' : 'text-(--color-foreground)',
					)}
				>
					{value}
				</span>
				<span className="text-sm font-[400] leading-5 text-(--color-muted-foreground)">{title}</span>
			</div>

			{clampedProgress != null && (
				<div className="h-1 w-full overflow-hidden rounded-full bg-(--color-muted)">
					<div
						className={cn(
							'h-full rounded-full transition-[width]',
							progressVariant === 'danger' ? 'bg-(--color-destructive)' : 'bg-(--color-foreground)',
						)}
						style={{ width: `${clampedProgress * 100}%` }}
					/>
				</div>
			)}

			{(footerLabel || footerValue) && (
				<div className="flex items-center">
					<span className="inline-flex items-center gap-1 rounded-md bg-(--color-muted) px-2 py-1 text-xs font-[500] leading-4 text-(--color-foreground)">
						{footerValue}
						{footerLabel && <span>{footerLabel}</span>}
					</span>
				</div>
			)}
		</div>
	)
}
